import { useState } from "react";
import { Link } from "react-router-dom";
import { Trash, ShieldWarning, EnvelopeSimple, ArrowRight, Lock, CheckCircle } from "@phosphor-icons/react";
import MarketingLayout from "@/components/MarketingLayout";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function DeleteAccount() {
  const { user } = useAuth();
  return (
    <MarketingLayout
      title="Delete your Nestled account"
      description="How to permanently delete your Nestled account and all your data. Self-serve from the app, or request deletion by email."
    >
      <article className="max-w-3xl mx-auto px-6 py-16">
        <div className="flex items-center gap-3 mb-3">
          <div className="h-12 w-12 rounded-2xl bg-terracotta-50 grid place-items-center">
            <Trash weight="duotone" size={26} className="text-terracotta-600" />
          </div>
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-stoneink-500">Account management</span>
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl text-stoneink-900 font-semibold tracking-tight">
          Delete your account
        </h1>
        <p className="mt-4 text-lg text-stoneink-700 leading-relaxed">
          You can permanently delete your Nestled account at any time. This page explains exactly how — and
          what data gets removed. Per Google Play and App Store policies, deletion is self-serve and free.
        </p>

        {/* Method 1: in-app */}
        <section className="mt-12 rounded-2xl border border-sand-300 bg-white p-7 shadow-ambient">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-terracotta-600 mb-2">
            <span className="h-5 w-5 rounded-full bg-terracotta-500 text-white grid place-items-center text-[10px]">1</span>
            Recommended — self-serve in the app
          </div>
          <h2 className="font-heading text-2xl text-stoneink-900 font-semibold tracking-tight mt-1">
            Delete from your Profile page
          </h2>
          <ol className="mt-5 space-y-3 text-stoneink-700 list-decimal pl-6 leading-relaxed">
            <li>Sign in to your Nestled account (in the mobile app or at <Link to="/login" className="text-terracotta-600 hover:underline">nestledapp.io</Link>).</li>
            <li>Open <strong>Profile</strong> from the menu.</li>
            <li>Scroll to the <strong>Danger zone</strong> section at the bottom of the page.</li>
            <li>Tap <strong>Delete my account</strong> and confirm with your password.</li>
          </ol>
          <div className="mt-6 flex flex-wrap gap-3">
            {user ? (
              <Link
                to="/profile"
                data-testid="delete-account-goto-profile"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-terracotta-600 hover:bg-terracotta-700 text-white font-medium transition-colors"
              >
                Go to my Profile <ArrowRight weight="bold" size={16} />
              </Link>
            ) : (
              <Link
                to="/login"
                data-testid="delete-account-goto-login"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-terracotta-600 hover:bg-terracotta-700 text-white font-medium transition-colors"
              >
                Sign in to continue <ArrowRight weight="bold" size={16} />
              </Link>
            )}
          </div>
        </section>

        {/* Method 2: email request */}
        <EmailRequestForm />

        {/* What gets deleted */}
        <section className="mt-12 rounded-2xl border border-ochre-400/40 bg-ochre-400/10 p-7">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-ochre-600 mb-2">
            <ShieldWarning weight="duotone" size={16} />
            What gets deleted
          </div>
          <h2 className="font-heading text-2xl text-stoneink-900 font-semibold tracking-tight mt-1">
            Permanent removal of all your data
          </h2>
          <p className="mt-3 text-stoneink-700 leading-relaxed">When you delete your account, the following are <strong>permanently removed</strong> from our database within 24 hours of the request:</p>
          <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-stoneink-700">
            {[
              "Your profile (name, email, password hash)",
              "All items you've created",
              "All rooms / locations you've created",
              "All categories and tags",
              "Photos attached to items / rooms",
              "Your household and invite codes",
              "Voice transcripts and search history",
              "Billing history (anonymized, retained 7 years per tax law)",
            ].map((row) => (
              <li key={row} className="flex items-start gap-2 text-sm">
                <CheckCircle weight="fill" size={16} className="text-terracotta-600 mt-0.5 flex-none" />
                <span>{row}</span>
              </li>
            ))}
          </ul>
          <p className="mt-5 text-sm text-stoneink-500 leading-relaxed">
            If you're part of a Family household owned by someone else, deleting your account just removes
            <strong className="text-stoneink-700"> your personal record</strong> — the shared household data created by the owner remains intact.
            If you <strong className="text-stoneink-700">own</strong> a Family household, deleting your account also deletes the entire household and removes all members.
          </p>
        </section>

        {/* Cancellation note */}
        <section className="mt-12 border-t border-sand-300 pt-8">
          <h2 className="font-heading text-xl text-stoneink-900 font-semibold">Active subscription?</h2>
          <p className="mt-2 text-stoneink-700 leading-relaxed">
            If you have a Pro or Family subscription, it is cancelled automatically when you delete your account.
            You will not be billed again. Any time remaining in your current billing period is forfeited.
          </p>
          <p className="mt-3 text-sm text-stoneink-500">
            Just want to cancel without deleting? Go to <Link to="/profile" className="text-terracotta-600 hover:underline">Profile → Billing</Link> and use the "Cancel subscription" button instead.
          </p>
        </section>

        {/* Contact footer */}
        <section className="mt-12 rounded-2xl bg-stoneink-900 text-white p-7 text-center">
          <div className="flex items-center justify-center gap-2 text-sm font-medium text-sand-200 mb-2">
            <Lock weight="duotone" size={16} /> Privacy questions?
          </div>
          <p className="text-sand-200">
            Email <a href="mailto:contact@nestledapp.io" className="text-white underline hover:text-terracotta-300">contact@nestledapp.io</a> and we'll respond within 2 business days.
          </p>
        </section>
      </article>
    </MarketingLayout>
  );
}

function EmailRequestForm() {
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSending(true);
    try {
      await api.post("/account-deletion-request", { email, reason });
      setSent(true);
      toast.success("Request received — we'll email you within 2 business days.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send request. Email contact@nestledapp.io instead.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <section className="mt-8 rounded-2xl border border-sage-400/40 bg-sage-400/10 p-7">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-sage-600 mb-2">
          <CheckCircle weight="duotone" size={16} /> Request received
        </div>
        <p className="text-stoneink-700">We've logged your deletion request for <strong>{email}</strong> and will process it within 2 business days. You'll get a confirmation email when it's done.</p>
      </section>
    );
  }

  return (
    <section className="mt-8 rounded-2xl border border-sand-300 bg-white p-7 shadow-ambient">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-stoneink-500 mb-2">
        <span className="h-5 w-5 rounded-full bg-stoneink-900 text-white grid place-items-center text-[10px]">2</span>
        Alternative — request by email
      </div>
      <h2 className="font-heading text-2xl text-stoneink-900 font-semibold tracking-tight mt-1">
        Can't sign in? Request deletion here
      </h2>
      <p className="mt-3 text-stoneink-700 leading-relaxed">
        Submit the form below and we'll verify your ownership of the email address, then delete the account within 2 business days.
      </p>
      <form onSubmit={submit} className="mt-5 space-y-4">
        <div>
          <label className="block text-sm font-medium text-stoneink-700 mb-1.5">Email on the account *</label>
          <input
            data-testid="delete-account-email-input"
            type="email" required value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="w-full px-4 h-11 rounded-xl bg-sand-100 border border-sand-300 focus:border-terracotta-500 outline-none"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-stoneink-700 mb-1.5">Reason (optional)</label>
          <textarea
            data-testid="delete-account-reason-input"
            rows={3} value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Helps us improve — totally optional."
            className="w-full px-4 py-3 rounded-xl bg-sand-100 border border-sand-300 focus:border-terracotta-500 outline-none"
          />
        </div>
        <button
          type="submit" disabled={sending || !email}
          data-testid="delete-account-submit"
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-stoneink-900 hover:bg-stoneink-700 text-white font-medium transition-colors disabled:opacity-50"
        >
          <EnvelopeSimple weight="duotone" size={16} /> {sending ? "Sending…" : "Submit deletion request"}
        </button>
      </form>
    </section>
  );
}
